import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AdminUser } from '@/lib/api.ts';
import { useMe } from '@/lib/queries.ts';

/**
 * Who gets what.
 *
 * BROWSER ONLY, and that is a design decision rather than an omission. A
 * television is not where you decide who in the household reaches the IPTV
 * subscription, and a remote is not the input device for it. The server
 * refuses anybody who is not an admin regardless of what a client shows, so
 * this screen not existing in the TV app costs nothing in security and saves
 * a whole class of "how did the eight-year-old get live TV".
 *
 * THE LIBRARY IS NOT HERE. Library access is Plex's own sharing settings —
 * every call is made with that person's token, so what they see is what you
 * shared with them, managed where you already manage it. A second set of
 * switches would be a second source of truth, and the day they disagree is
 * the day somebody sees a library you thought you had removed.
 */
export function Accounts() {
  const { data: me } = useMe();
  const qc = useQueryClient();

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.adminUsers(),
    staleTime: 10_000,
  });

  const setCap = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<AdminUser['can']> }) =>
      api.setCapabilities(id, patch),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });

  if (isLoading) return <div className="muted center">Loading accounts…</div>;
  if (error) return <div className="muted center">Could not load accounts.</div>;

  return (
    <>
      <p className="muted admin-note">
        Anyone you share your Plex server with can sign in. What they reach here is
        live TV, radio, SiriusXM and the Games shelf — the things Plex knows nothing about.
        <strong> Library access is Plex’s own sharing settings</strong>, managed in Plex.
      </p>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Live TV</th>
            <th>Radio</th>
            <th>SiriusXM</th>
            <th>Games</th>
            <th>360/PS3</th>
            <th>Admin</th>
          </tr>
        </thead>
        <tbody>
          {(users ?? []).map((u) => (
            <tr key={u.id}>
              <td>
                <div className="admin-name">{u.displayName}</div>
                <div className="muted admin-sub">
                  {u.username}
                  {!u.linked && <span className="admin-unlinked"> · needs to sign in again</span>}
                </div>
              </td>
              {(['liveTv', 'radio', 'sxm', 'games', 'pcGames', 'admin'] as const).map((key) => (
                <td key={key}>
                  <input
                    type="checkbox"
                    checked={u.can[key]}
                    /* An admin removing their own admin would leave nobody
                       able to grant it back — the server refuses it too, but
                       there is no reason to offer the click. */
                    disabled={setCap.isPending || (key === 'admin' && u.id === me?.user.id)}
                    onChange={(e) =>
                      setCap.mutate({ id: u.id, patch: { [key]: e.target.checked } })}
                    aria-label={`${key} for ${u.displayName}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {setCap.isError && (
        <p className="admin-error">{(setCap.error as Error).message}</p>
      )}

      <SeerrLogins users={users ?? []} />
    </>
  );
}

/**
 * WHO EACH PROFILE IS INSIDE SEERR.
 *
 * A managed Plex Home profile has no plex.tv account, so Seerr can never
 * authenticate it — measured, not assumed: it answers 500 "Unable to
 * authenticate" every time. Such a profile therefore borrows the identity of
 * whoever switched into it, and its requests are filed under that person and
 * spend that person's quota.
 *
 * Giving it a local Seerr account is the way out, and it is worth the trouble
 * for anybody who actually asks for things: their own request history, their
 * own quota, their own name against what they asked for.
 *
 * This is the only place in the app that holds a password for another
 * service, which is a real departure — everything else delegates. It is here
 * because there is no delegating available: there is nothing on plex.tv to
 * delegate TO. The password is sealed in the database and never comes back
 * out of it, not even to the admin who typed it.
 */
function SeerrLogins({ users }: { users: AdminUser[] }) {
  /*
   * The ones where the question arises: managed profiles, plus anybody
   * already set up. An account with its own Plex login signs in to Seerr by
   * itself and has no business in this list.
   */
  const rows = users.filter((u) => u.managed || u.seerrEmail);
  if (!rows.length) return null;

  return (
    <section className="admin-section">
      <h3 className="admin-subhead">Seerr sign-in</h3>
      <p className="muted admin-note">
        Profiles that share your Plex login have no Plex account of their own, so Seerr
        cannot sign them in. Without an account here they can still search and request —
        but it is recorded as <strong>you</strong>, and spends your request allowance.
        Give one a local Seerr account and it becomes itself again.
      </p>

      <div className="seerr-logins">
        {rows.map((u) => <SeerrLoginRow key={u.id} user={u} />)}
      </div>
    </section>
  );
}

function SeerrLoginRow({ user }: { user: AdminUser }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState(user.seerrEmail ?? '');
  const [password, setPassword] = useState('');

  const save = useMutation({
    mutationFn: () => api.setSeerrLogin(user.id, email.trim(), password),
    onSuccess: () => {
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const clear = useMutation({
    mutationFn: () => api.setSeerrLogin(user.id, '', ''),
    onSuccess: () => {
      setEmail('');
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const busy = save.isPending || clear.isPending;
  /* Changing the address is changing the account, so it needs the password
     that goes with it. Leaving both alone and pressing Save is a no-op the
     server treats as "keep what is there". */
  const needsPassword = !!email.trim() && (!user.seerrEmail || email.trim() !== user.seerrEmail);

  return (
    <div className="seerr-login">
      <div className="seerr-login-who">
        <div className="admin-name">{user.displayName}</div>
        <div className="muted admin-sub">
          {user.seerrEmail
            ? 'signs in as themselves'
            : 'requests are filed under the household account'}
        </div>
      </div>

      <input
        className="seerr-input"
        type="email"
        value={email}
        placeholder="seerr email address"
        autoComplete="off"
        disabled={busy}
        onChange={(e) => setEmail(e.target.value)}
        aria-label={`Seerr email for ${user.displayName}`}
      />
      <input
        className="seerr-input"
        type="password"
        value={password}
        /* Says what leaving it blank MEANS, rather than leaving somebody to
           wonder whether an empty box wipes what is stored. */
        placeholder={user.seerrEmail ? 'password (blank = unchanged)' : 'seerr password'}
        autoComplete="new-password"
        disabled={busy}
        onChange={(e) => setPassword(e.target.value)}
        aria-label={`Seerr password for ${user.displayName}`}
      />

      <button
        className="btn"
        disabled={busy || (needsPassword && !password)}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
      {user.seerrEmail && (
        <button className="btn" disabled={busy} onClick={() => clear.mutate()}>
          Remove
        </button>
      )}

      {(save.isError || clear.isError) && (
        <div className="admin-error seerr-login-msg">
          {((save.error ?? clear.error) as Error).message}
        </div>
      )}
    </div>
  );
}
